import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Target, ShieldCheck, HeartHandshake } from "lucide-react";
import { AnimatedWords } from "@/components/motion";

const FEATURES = [
  {
    icon: Target,
    title: "Goal-Based Matching",
    body: "Create a profile, swipe through potential partners, and match with people who share your goals, experience level and location — so every session pushes you forward.",
    span: "",
  },
  {
    icon: ShieldCheck,
    title: "Verified Members",
    body: "Train with confidence. Every member is verified, so you can match and meet up knowing your gym partner is who they say they are.",
    span: "",
  },
  {
    icon: HeartHandshake,
    title: "Real Accountability",
    body: "A partner who shows up gives you that extra push on the days you don't feel motivated. Consistency, built in.",
    span: "",
  },
];

export default function Features() {
  return (
    <section id="features" data-testid="features-section" className="relative overflow-hidden bg-[#0A0A0A] py-24 md:py-32">
      {/* Kinetic marquee background */}
      <div className="pointer-events-none absolute top-1/2 left-0 w-full -translate-y-1/2 select-none opacity-[0.9]">
        <Marquee speed={40} gradient={false}>
          <span className="text-stroke font-display text-[12vw] leading-none uppercase">
            TRAINOVA&nbsp;•&nbsp;FIND YOUR PARTNER&nbsp;•&nbsp;MATCH GOALS&nbsp;•&nbsp;
          </span>
        </Marquee>
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="mb-10 flex items-center gap-3">
          <span className="h-px w-10 bg-teal" />
          <span className="font-condensed text-base tracking-[0.35em] text-teal">WHY TRAINOVA</span>
        </div>

        <h2 className="mb-6 font-display text-5xl leading-[0.9] uppercase sm:text-6xl md:text-7xl">
          <AnimatedWords text="Built for people" />
          <br />
          <span className="accent-teal">
            <AnimatedWords text="who show up" delay={0.2} />
          </span>
        </h2>

        <p className="mb-16 max-w-2xl text-base text-zinc-400 md:text-lg">
          Trainova is a swipe-based fitness app. Find your match in a few taps, then take it
          to the gym floor.
        </p>

        <div className="grid auto-rows-fr gap-4 md:grid-cols-3">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                data-testid={`feature-card-${i}`}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 }}
                className={`group relative flex flex-col border border-white/10 bg-[#101010] p-8 transition-colors duration-300 hover:border-teal ${f.span}`}
              >
                <div className="mb-16 flex h-14 w-14 items-center justify-center border border-white/10 text-teal transition-colors duration-300 group-hover:bg-teal group-hover:text-black">
                  <Icon size={26} strokeWidth={1.8} />
                </div>
                <div>
                  <h3 className="font-condensed text-3xl tracking-wide md:text-4xl">{f.title}</h3>
                  <p className="mt-3 max-w-md text-base leading-relaxed text-zinc-400">{f.body}</p>
                </div>
                <span className="absolute right-6 top-6 font-display text-2xl text-white/10">
                  0{i + 1}
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
